/// <amd-module name="Test/TestPlansHubRefresh/TestCasesImportModule/Component/TestCasesImportPanelComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import { TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { SuitesActions } from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesActions";
import * as Resources from "Test/TestPlansHubRefresh/TestCasesImportModule/Resources";
import * as Constants from "../Constants";
import { TestCasesImportPanelModule } from "Test/TestPlansHubRefresh/TestCasesImportModule/redux/TestCasesImportPanelModule";
import { ITestCasesImportPanelAwareState } from "Test/TestPlansHubRefresh/TestCasesImportModule/Redux/TestCasesImportPanelContracts";
import { TestCasesImportPanelActions } from "Test/TestPlansHubRefresh/TestCasesImportModule/Redux/TestCasesImportPanelActions";
import { TestCaseCSVImportUtility } from "../Util/TestCaseCSVImportUtility";
import { ObservableValue } from "VSS/Core/Observable";
import * as Util from "VSS/Platform/Util/Cookie";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Dialog } from "VSSUI/Dialog";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { MessageCard } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelCloseButton, PanelContent, PanelFooter } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { Components, IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { HtmlUtils } from "Test/TestPlansHubRefresh/TestCaseWorkItemControls/TestSteps/TestStepsUtils/HtmlUtils";
import * as XLSX from "xlsx";

export interface ITestCasesImportProps extends IVssComponentProperties {
    showPanel: ObservableValue<boolean>;
    planId: number;
    suite: any;
    areaPath: string;
    iterationPath: string;
}

export interface ITestCasesImportOwnProps {
    isCSVFileAdded: boolean;
    isLoading: boolean;
    percentComplete: number;
    errorMessages: string;
    csvFileAdded: (isCSVFileAdded: boolean) => void;
    updateLoadingState: (isLoading: boolean, percentComplete: number) => void;
    showErrorMessages: (error: string) => void;
    reloadTestCasesForSelectedSuite: (planId: number, suiteId: number, suiteName: string) => void;
    showToastNotifications: (message: string, duration: number) => void;
}

/**
 * TestCasesImportPanelComponent: Renders the panel for importing test cases to a suite
 */
export class TestCasesImportPanelComponent extends VssComponent<ITestCasesImportProps & ITestCasesImportOwnProps, IVssComponentContext> {
    private _csvString: string;
    private _errorMessageString: string = "";
    private _isImportCompleted: boolean = false;
    private _testCasesUpdated: number = 0;
    private _testCasesCreated: number = 0;
    private _isConfirmationDialogOpen = new ObservableValue<boolean>(false);
    private _confirmDialogCheckBox = new ObservableValue<boolean>(false);
    private _fileExtension: string = "csv";
    private _fileSize: number = 0;

    constructor(props: ITestCasesImportProps & ITestCasesImportOwnProps, context?: IVssComponentContext) {
        super(props, context);
        this._csvString = "";
        this._isConfirmationDialogOpen.value = false;
    }

    public render(): JSX.Element {
        let container: JSX.Element = <></>;
        if (this.props.isLoading) {
            container = <Spinner className="import-spinner flex-grow" label={String(this.props.percentComplete) + "%"} />;
        } else if (this.props.errorMessages !== "") {
            container = <MessageCard className="flex-self-stretch">{this.props.errorMessages}</MessageCard>;
        } else {
            const button = React.createElement("input", {
                type: "file",
                accept:".csv,.xlsx",
                onChange: (e) => {
                    if (e && e.target && e.target.files) {
                        this.filterFilesAndUpdateCSVString(e.target.files);
                    }
                }
            });
            container = React.createElement("div", { className: "import-testcases-input-button" }, button);
        }
        const isDisabled = !this.props.isCSVFileAdded || this.props.isLoading || this.props.errorMessages !== "";
        return (
            <DynamicModuleLoader modules={[TestCasesImportPanelModule]}>
                <Observer isDialogOpen={this._isConfirmationDialogOpen}>
                    {(props: { isDialogOpen: boolean }) => {
                        return props.isDialogOpen ? (
                            <Dialog
                                titleProps={{ text: Resources.ConfirmImport }}
                                footerButtonProps={[
                                    {
                                        text: Resources.CancelButton,
                                        onClick: this._hideConfirmationDialog
                                    },
                                    {
                                        text: Resources.ConfirmButton,
                                        onClick: this._startImport,
                                        primary: true
                                    }
                                ]}
                                onDismiss={this._hideConfirmationDialog}
                            >
                                {Resources.DialogText}
                                <Checkbox
                                    onChange={(event, checked) => (this._confirmDialogCheckBox.value = checked)}
                                    checked={this._confirmDialogCheckBox}
                                    label={Resources.ConfirmDialogCheckboxLabel}
                                />
                            </Dialog>
                        ) : null;
                    }}
                </Observer>
                <CustomPanel lightDismiss={false} onDismiss={this._hidePanel}>
                    <CustomHeader>
                        <HeaderTitleArea>
                            <div className="import-panel-header title-m">{Resources.ImportTestCases}</div>
                            <div className="import-panel-subheading">{Resources.ImportPanelSubHeadingCsvOrExcel}</div>
                        </HeaderTitleArea>
                        <PanelCloseButton onDismiss={this._hidePanel} />
                    </CustomHeader>
                    <PanelContent>
                        <div>{container}</div>
                    </PanelContent>
                    <PanelFooter>
                        <div className="import-panel-footer">
                            <Button ariaLabel="import" disabled={isDisabled} onClick={this._onImportClick} primary={true}>
                                {Resources.ImportButton}
                            </Button>
                            <Button ariaLabel="cancel" onClick={this._hidePanel} className="import-cancel-button">
                                {Resources.CancelButton}
                            </Button>
                        </div>
                    </PanelFooter>
                </CustomPanel>
            </DynamicModuleLoader>
        );
    }

    private _startImport = () => {
        this._hideConfirmationDialog();
        this._setConfirmationDialogCookie();
        const testCaseImportUtil = new TestCaseCSVImportUtility();
        this.props.updateLoadingState(true, 0);
        const plan = {
            id: this.props.planId,
            areaPath: this.props.areaPath,
            iterationPath: this.props.iterationPath
        };
        testCaseImportUtil
            .importCSVFile(
                this._csvString,
                this.context.pageContext,
                plan,
                this.props.suite,
                (percentComplete: number, testCasesUpdated: number, testCasesCreated: number, errorMessage?: string) => {
                    if (errorMessage) {
                        this._errorMessageString += errorMessage + "\n";
                        this._publishErrorTelemetry(errorMessage);
                    }
                    if (percentComplete == 100) {
                        this._isImportCompleted = true;
                        this._testCasesCreated = testCasesCreated;
                        this._testCasesUpdated = testCasesUpdated;
                        if (this._errorMessageString === "") {
                            this._hidePanel();
                        } else {
                            this.props.showErrorMessages(Resources.ErrorMessage);
                        }
                    } else {
                        this.props.updateLoadingState(true, percentComplete);
                    }
                },
                (error) => {
                    this._errorMessageString += error + "\n";
                    this._publishErrorTelemetry(error);
                },
                this._fileExtension
            )
            .then()
            .catch((reason) => {
                this.props.showErrorMessages(reason);
                this._publishErrorTelemetry(reason);
            });
    };

    private _onImportClick = () => {
        const hideDialog: string = Util.getCookie(Constants.hideConfirmationDialogCookie);
        if (hideDialog !== "true") {
            this._isConfirmationDialogOpen.value = true;
        } else {
            this._startImport();
        }
    };

    private _hideConfirmationDialog = () => {
        this._isConfirmationDialogOpen.value = false;
    };

    private _setConfirmationDialogCookie = () => {
        if (this._confirmDialogCheckBox.value) {
            Util.setCookie(Constants.hideConfirmationDialogCookie, String(this._confirmDialogCheckBox.value));
        }
    };

    private _hidePanel = () => {
        this.props.showPanel.value = false;
        if (this._isImportCompleted) {
            this.props.showToastNotifications(
                this._testCasesUpdated + Resources.TestCasesUpdated + this._testCasesCreated + Resources.TestCasesCreated,
                8000
            );
            this.props.reloadTestCasesForSelectedSuite(this.props.planId, this.props.suite.id, this.props.suite.name);

            const telemetryPayload = {
                importFileType : this._fileExtension,
                planId: this.props.planId,
                suiteId: this.props.suite.id,
                testCasesCreated: this._testCasesCreated,
                testCasesUpdated: this._testCasesUpdated
            };
            this.props.publishTelemetry(TelemetryConstants.IMPORT_TESTCASES_COMPLETED, telemetryPayload);
        }
    };

    private _publishErrorTelemetry = (errorMessage: string) => {
        const telemetryPayload = {
            importFileType : this._fileExtension,
            planId: this.props.planId,
            suiteId: this.props.suite.id,
            errorMessage: errorMessage
        };
        this.props.publishTelemetry(TelemetryConstants.IMPORT_TESTCASES_ERROR, telemetryPayload);
    }

    private _processCSVFileAndUpdateCSVString(csvFile: File) {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader && reader.result) {
                const result: string = reader.result.toString();
                this._csvString = result;
                this.props.csvFileAdded(true);
            }
        };

        reader.readAsText(csvFile);
        this._fileSize = csvFile.size;

        const telemetryPayload = {
            importFileType : this._fileExtension,
            planId: this.props.planId,
            suiteId: this.props.suite.id,
            fileSize: this._fileSize
        };
        this.props.publishTelemetry(TelemetryConstants.SELECT_FILE_IMPORT_TESTCASES, telemetryPayload);
    }

    private _containsHTMLTags(input: string): boolean {
        const htmlTagPattern = /<[^>]*>/; // Regular expression to match HTML tags

        return htmlTagPattern.test(input);
    }

    private _processXLSXFileAndUpdateCSVString(xlsxFile: File) {
        const reader = new FileReader();

        reader.onload = () => {
            if (reader && reader.result) {
                let workbook: XLSX.WorkBook | undefined;
                let readerResult = reader.result as ArrayBuffer;
                workbook = XLSX.read(readerResult);
                this._fileSize = readerResult.byteLength;

                if (workbook) {
                    // Get the first worksheet
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]];

                    // Get the range of cells in the worksheet
                    let range = XLSX.utils.decode_range(worksheet["!ref"] as string);

                    // Convert data from excel file to csv format for further processing
                    let csvStringFromExcelFileData = '';

                    // Find all headers in excel sheet assuming that first row contains all headers
                    type SheetHeaders = {
                        [key: string]: number;
                    };

                    const sheetHeaders: SheetHeaders = {};

                    for (let row = range.s.r; row <= range.s.r; row++) {
                        for (let col = range.s.c; col <= range.e.c; col++) {
                            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                            let cellObject = worksheet[cellAddress];
                            if (cellObject && cellObject.w) {
                                sheetHeaders[cellObject.w.toLowerCase()] = col;
                            }
                        }
                    }

                    for (let row = range.s.r; row <= range.e.r; row++) {
                        for (let col = range.s.c; col <= range.e.c; col++) {
                            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                            let cellValue = '';

                            // Fetch data from a cell in HTML format if avaiable else fetch the data without formatting
                            let cellObject = worksheet[cellAddress];

                            if (cellObject && cellObject.w) {
                                let cellContainsHTMLTags = this._containsHTMLTags(cellObject.w);
                                let isStepsCell = col === sheetHeaders["step action"] || col === sheetHeaders["step expected"];
                                /* If cell is not a test steps cell, then read the cell value, e.g. Title, Work Item Type, Priority, etc.
                                *  If cell is a test steps cell and the cell doesn't contains html tags, then read the cell value without formatting
                                *  If cell is a test steps cell and the cell contains html tags, then read the cell value in html format
                                */
                                if (!isStepsCell) {
                                    cellValue = cellObject.w;
                                } else if (cellObject.h && !cellContainsHTMLTags) {
                                    cellValue = cellObject.h;
                                } else {
                                    cellValue = HtmlUtils.encodeLeftAngularBracket(cellObject.w);
                                }
                            }

                            // Replace double quotes used in cell values with double double quotes as the double quotes are used as a delimiter in csv format
                            cellValue = cellValue.replace(/"/g, '""');

                            // Convert the cell value to string and add it to the csv string
                            csvStringFromExcelFileData += `"${cellValue}"`;
                            // Add a comma if the cell is not the last cell in the row
                            if (col < range.e.c) {
                                csvStringFromExcelFileData += ',';
                            }
                        }

                        // Add a newline after each row
                        csvStringFromExcelFileData += '\n';
                    }

                    this._csvString = csvStringFromExcelFileData;
                    this.props.csvFileAdded(true);
                }
            }
        };
        reader.readAsArrayBuffer(xlsxFile);

        const telemetryPayload = {
            importFileType : this._fileExtension,
            planId: this.props.planId,
            suiteId: this.props.suite.id,
            testCasesCreated: this._testCasesCreated,
            testCasesUpdated: this._testCasesUpdated,
            fileSize: this._fileSize
        };
        this.props.publishTelemetry(TelemetryConstants.SELECT_FILE_IMPORT_TESTCASES, telemetryPayload);

    }

    public filterFilesAndUpdateCSVString(files: FileList): void {
        
        let csvFile = undefined;
        let xlsxFile = undefined;

        for (let i = 0; i < files.length; i++) {
            let fileExtension = files[i].name.split(".").pop();

            if (fileExtension === undefined) {
                this._fileExtension = "";
            } else {
                this._fileExtension = fileExtension as string;
            }
            
            if (this._fileExtension === "csv") {
                csvFile = files[i];
                break;
            }

            if (this._fileExtension === "xlsx") {
                xlsxFile = files[i];
                break;
            }
        }

        if (csvFile === undefined && xlsxFile === undefined) {
            return;
        }

        if (csvFile) {
            this._processCSVFileAndUpdateCSVString(csvFile);
        } else if (xlsxFile) {
            this._processXLSXFileAndUpdateCSVString(xlsxFile);
        }
    }
}

function mapStateToProps(state: ITestCasesImportPanelAwareState): Partial<ITestCasesImportProps & ITestCasesImportOwnProps> {
    return {
        isCSVFileAdded: state.testCasesImportPanelState ? state.testCasesImportPanelState.csvFileAdded : false,
        isLoading: state.testCasesImportPanelState && state.testCasesImportPanelState.isLoading ? state.testCasesImportPanelState.isLoading : false,
        errorMessages:
            state.testCasesImportPanelState && state.testCasesImportPanelState.errorMessages ? state.testCasesImportPanelState.errorMessages : "",
        percentComplete:
            state.testCasesImportPanelState && state.testCasesImportPanelState.percentComplete ? state.testCasesImportPanelState.percentComplete : 0
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestCasesImportProps & ITestCasesImportOwnProps> {
    return {
        publishTelemetry: (action: string, payload: { [key: string]: any }) => dispatch(TelemetryActions.publishTelemetry(action, payload)),
        csvFileAdded: (isCSVFileAdded: boolean) => dispatch(TestCasesImportPanelActions.csvFileAdded(isCSVFileAdded)),
        updateLoadingState: (isLoading: boolean, percentComplete: number) =>
            dispatch(TestCasesImportPanelActions.updateLoadingState(isLoading, percentComplete)),
        showErrorMessages: (error: string) => dispatch(TestCasesImportPanelActions.showErrorMessages(error)),
        reloadTestCasesForSelectedSuite: (planId: number, selectedSuiteId: number, selectedSuiteName: string) =>
            dispatch(SuitesActions.changeSelectedSuite(planId, selectedSuiteId, selectedSuiteName)),
        showToastNotifications: (message: string, duration: number) => dispatch(SuitesActions.showToastNotifications(message, duration)),
        dispatch
    };
}

export const TestCasesImportPanelConnectedView = connect(mapStateToProps, mapDispatchToProps)(TestCasesImportPanelComponent);

Components.add("testcases-import-panel", TestCasesImportPanelConnectedView);
